import { RateMapper } from "../../../rate/rate.mapper";
import { RouteMapper } from "../../../route/route.mapper";
import { Route } from "../../../route/route-type";
import { User } from "../../../user/user.model";
import { Carrier } from "../../../carrier/carrier.model";
import { format } from "date-fns";
import { LclRate } from "../../../rate/rate-lcl.model";
import { AirRate } from "../../../rate/rate-air.model";
import { BaseLinkedRate } from "../../../rate/rate-base-linked.model";
import { ITableRow } from "../../../ui/table/table-row.interface";
import { LtlRate } from "../../../rate/rate-ltl.model";
import { LtlLinkedRate } from "../../../rate/rate-ltl-linked.model";
import { LtlRoute } from "app/route/route-ltl.model";
import { rateSource } from "app/rate/rate-source.const";
import { FtlFlatRate } from "app/rate/rate-ftl-flat.model";
import { PtlFlatRate } from "app/rate/rate-ptl-flat.model";

export class FreightRate implements ITableRow {
  private static keySeq = 0;

  // Unique per instance: online rates have no rate/route id yet, so `id` cannot
  // identify a row until it is persisted.
  public rowKey = `fr-${FreightRate.keySeq++}`;

  constructor(
    public rate?: BaseLinkedRate,
    public route?: Route,
    public flatRateRouteId?: number,
    public pickupRateRouteId?: number,
    public deliveryRateRouteId?: number,
    public restrictedNote?: string,
    public failedRestrictions?: string,
    public quoteOriginLocationId?: number,
    public quoteDestinationLocationId?: number,
    public isBusy?: boolean,
    public externalLink?: string,
    public validityNote?: string,
  ) {}

  public getId(): number {
    return this.id;
  }

  public setId(id: number): void {
    return null;
  }

  public isCreator(user: User): boolean {
    return this.rate.getCreatedBy().getId() === user.getId();
  }

  public getRate(): BaseLinkedRate {
    return this.rate;
  }

  public getRoute(): Route {
    return this.route;
  }

  public getFlatRateRouteId(): number {
    return this.flatRateRouteId;
  }

  public getPickupRateRouteId(): number {
    return this.pickupRateRouteId;
  }

  public getDeliveryRateRouteId(): number {
    return this.deliveryRateRouteId;
  }

  public getQuoteOriginLocationId(): number {
    return this.quoteOriginLocationId;
  }

  public getQuoteDestinationLocationId(): number {
    return this.quoteDestinationLocationId;
  }

  public setRoute(route: Route | null): void {
    this.route = route;
  }

  public haveRoute(): boolean {
    return this.getRoute() && !!this.getRoute().getId();
  }

  public getClone(): FreightRate {
    return new FreightRate(
      <BaseLinkedRate>RateMapper.map(this.getRate()),
      RouteMapper.map(this.getRoute()),
      this.getFlatRateRouteId(),
      this.getPickupRateRouteId(),
      this.getDeliveryRateRouteId(),
      this.restrictedNote,
      this.failedRestrictions,
      this.quoteOriginLocationId,
      this.quoteDestinationLocationId,
      this.isBusy,
      this.externalLink,
      this.validityNote,
    );
  }

  public isExpiredSpotRate(): boolean {
    return this.rate.isSpotRate() && this.rate.isExpired();
  }

  public isValid(): boolean {
    if (this.isExpiredSpotRate()) {
      return false;
    }

    if (this.rate instanceof LtlRate) {
      return (
        !!this.rate.getName() &&
        !!this.rate.getValidity() &&
        !!this.rate.getChargeableWeightPrice() &&
        !!this.rate.getCurrency()
      );
    } else if (this.rate instanceof FtlFlatRate) {
      return (
        !!this.rate.getName() &&
        !!this.rate.getCarrier() &&
        !!this.rate.getValidity()
      );
    } else if (this.rate instanceof PtlFlatRate) {
      return !!this.rate.getName() && !!this.rate.getValidity();
    } else {
      return (
        !!this.rate.getName() &&
        !!this.rate.getCarrier() &&
        !!this.rate.getValidity() &&
        !!this.rate.getCurrency()
      );
    }
  }

  /* TABLE COMPONENT - GLOBAL METHODS */
  public getDiscriminator(): string {
    return this.getRate().getDiscriminator();
  }

  public get id(): number {
    return this.route ? this.route.getId() : this.rate.getId();
  }

  public get name(): string {
    return this.rate.getName();
  }

  public set name(param: string) {
    this.rate.setName(param);
  }

  public get carrier(): Carrier {
    return this.rate.getCarrier();
  }

  public set carrier(param: Carrier) {
    this.rate.setCarrier(param);
  }

  public get serviceLabel(): string {
    const carrierName = this.carrier?.getName() || "All";
    const agentName = this.rate.getAgent()?.getName();

    return agentName ? `${carrierName} [${agentName}]` : carrierName;
  }

  public get currency(): string {
    return this.rate.getCurrency();
  }

  public set currency(param: string) {
    this.rate.setCurrency(param);
  }

  public get internalNote(): string {
    return this.rate.getInternalNote()?.trim();
  }

  public set internalNote(param: string) {
    this.rate.setInternalNote(param);
  }

  public get validity() {
    return this.rate.getValidity();
  }

  public set validity(param) {
    this.rate.setValidity(param);
  }

  public get debugInfo(): string {
    const rate = this.getRate();
    if (rate instanceof LtlRate || rate instanceof LtlLinkedRate) {
      let debugInfo = rate.debugInfo;
      if (debugInfo) {
        debugInfo = debugInfo.replace(
          /{{r:([0-9]+)}}{{ro:([0-9]+)}}{{wb:([0-9a-z]+)}}/gm,
          "<a href='/rate/fcl/contract/$1?route=$2&weightBracket=$3' target='_blank'><i class='pi pi-external-link'></i></a>",
        );
        debugInfo = debugInfo.replace(
          /{{r:([0-9]+)}}/gm,
          "<a href='/rate/fcl/contract/$1' target='_blank'><i class='pi pi-external-link'></i></a>",
        );
        debugInfo = debugInfo.replace(
          /{{mrqr:([0-9]+)}}/gm,
          "<a href='/margin-rule/rule-quote-rate/$1' target='_blank'><i class='pi pi-external-link'></i></a>",
        );
      }

      return debugInfo;
    }
  }

  public get conversionRatio(): string | null {
    const rate = this.getRate();
    if (
      rate instanceof LclRate &&
      rate.getWeightPrice() &&
      rate.getMeasurementPrice()
    ) {
      return `1 CBM = ${rate.getConversionRatio()} KG`;
    }

    return null;
  }

  // Shown on the Validity cell itself rather than in the row's warning column,
  // because it is the date range that has to change.
  public get validityWarningMessage(): string {
    if (!this.isExpiredSpotRate()) {
      return this.validityNote ?? "";
    }

    const rate = this.getRate();

    // A rate being created cannot have "expired" yet, nor be deselected.
    return rate.getId()
      ? `This spot rate expired on ${format(
          rate.validTill,
          "d.MM.yy",
        )}. Extend its validity or deselect it.`
      : "Spot rate validity cannot end in the past.";
  }

  public get warningMessage(): string {
    let warningMessage = this.failedRestrictions
      ? `${this.failedRestrictions}`
      : "";
    const rate = this.getRate();
    const route = this.getRoute();

    if (
      (rate instanceof LtlRate || rate instanceof LtlLinkedRate) &&
      route instanceof LtlRoute
    ) {
      const ratePrice = rate.getChargeableWeightMargin() * 1;
      const routePrice = route.getChargeableWeightPrice() * 1;
      if (rate.getChargeableWeightMargin() && ratePrice !== routePrice) {
        warningMessage += warningMessage ? "<br/>" : "";
        warningMessage +=
          "Margin is different than the default one. Consider deselecting and selecting the rate again.";
      }
    }

    if (warningMessage) {
      warningMessage = warningMessage.replace(
        /{{r:([0-9]+)}}{{ro:([0-9]+)}}{{wb:([0-9a-z]+)}}/gm,
        "<a href='/rate/fcl/contract/$1?route=$2&weightBracket=$3' target='_blank'><i class='pi pi-external-link'></i></a>",
      );
      warningMessage = warningMessage.replace(
        /{{r:([0-9]+)}}/gm,
        "<a href='/rate/fcl/contract/$1' target='_blank'><i class='pi pi-external-link'></i></a>",
      );
    }

    return warningMessage;
  }

  public get noMarginPrice(): number {
    const rate = this.getRate();
    if (rate instanceof LtlRate || rate instanceof LtlLinkedRate) {
      return rate.getNoMarginPrice();
    }
  }

  public get departureDate(): Date {
    const rate = this.getRate();
    if (rate instanceof LtlRate || rate instanceof LtlLinkedRate) {
      return rate.getDepartureDate();
    }
  }

  public get deliveryDate(): Date {
    const rate = this.getRate();
    if (rate instanceof LtlRate || rate instanceof LtlLinkedRate) {
      return rate.getDeliveryDate();
    }
  }

  public get info(): string {
    const rate = this.getRate();
    let info = "";
    const infoHeader = [];

    if (rate.isExtendedRate() && !!rate.getRootParent()) {
      infoHeader.push(`Extended from: ${rate.getRootParent().getName()}`);
    }

    if (rate.getUpdatedAt()) {
      infoHeader.push(
        `Updated at ${format(rate.getUpdatedAt(), "yyyy-MM-dd")}`,
      );
    }

    if (rate.getUpdatedBy() && rate.getUpdatedBy().getFullName()) {
      infoHeader.push(`Updated by ${rate.getUpdatedBy().getFullName()}`);
    } else if (rate.getCreatedBy() && rate.getCreatedBy().getFullName()) {
      infoHeader.push(`Created by ${rate.getCreatedBy().getFullName()}`);
    }

    info = infoHeader.join(", ");
    if (info) {
      info += "<br/>";
    }

    if (rate.getContractNumber() && rate.getSource() !== rateSource.online) {
      info += `Contract Number: ${rate.getContractNumber()}<br/>`;
    }

    const internalNote = rate.getInternalNote()
      ? `<br/>${rate.getInternalNote()}`
      : "";

    return info ? `${info}${internalNote}` : internalNote;
  }

  /* TABLE COMPONENT - LCL METHODS */
  public get chargeableWeight(): number {
    return this.rate instanceof LclRate || this.rate instanceof AirRate
      ? this.rate.getChargeableWeight()
      : 0;
  }

  public set chargeableWeight(param: number) {
    if (this.rate instanceof LclRate || this.rate instanceof AirRate) {
      this.rate.setChargeableWeight(param);
    }
  }

  public get minPrice(): number {
    return this.rate instanceof LclRate || this.rate instanceof AirRate
      ? this.rate.getMinPrice()
      : 0;
  }

  public get weightPriceLt45(): number {
    return this.rate instanceof AirRate ? this.rate.getWeightPriceLt45() : 0;
  }

  public get weightPriceGt45(): number {
    return this.rate instanceof AirRate ? this.rate.getWeightPriceGt45() : 0;
  }

  public get weightPriceGt100(): number {
    return this.rate instanceof AirRate ? this.rate.getWeightPriceGt100() : 0;
  }

  public get weightPriceGt300(): number {
    return this.rate instanceof AirRate ? this.rate.getWeightPriceGt300() : 0;
  }

  public get weightPriceGt500(): number {
    return this.rate instanceof AirRate ? this.rate.getWeightPriceGt500() : 0;
  }

  public get weightPriceGt1000(): number {
    return this.rate instanceof AirRate ? this.rate.getWeightPriceGt1000() : 0;
  }

  public set minPrice(param: number) {
    if (this.rate instanceof LclRate || this.rate instanceof AirRate) {
      this.rate.setMinPrice(param);
    }
  }

  public get comment(): string {
    if (this.route && this.route.getComment()) {
      return this.route.getComment();
    } else {
      return this.rate.getComment();
    }
  }

  public set comment(param: string) {
    this.rate.setComment(param);
    if (this.route) {
      this.route.setComment(param);
    }
  }
}
